import { defineStore } from 'pinia';
import { useDeliveryStore } from './delivery';
import { useBrandStore } from './brand';
import { STANDARD_DELIVERIES } from '@/config/delivery-config.js';
import { StoreLocation } from '~/types/store';

type Store = {
  id: string;
  address: string;
  city: string;
  phone?: string;
  postalCode: string;
  name: string;
  deliveryServices?: string[];
  contact?: string;
  address2?: string;
  erpAddressId?: string;
  email?: string;
};

export type ErpStoreDetails = {
  id: string;
  name: string;
  address: string;
  city: string;
  postalCode: string;
  phone: string;
  email: string;
};

export const useStoresStore = defineStore('stores', () => {
  const centralStorageId = ref<string>('');
  const storeIdMap = ref<{ [key: string]: StoreLocation }>({});
  const storeList = ref<Store[]>([]);
  const defaultStore = ref<Store | {}>({});

  const setCentralStorageId = (id: string): void => {
    centralStorageId.value = id;
  };

  const setStoreListFromLocationApi = async (): Promise<StoreLocation[]> => {
    const { $locationService } = useNuxtApp();
    const { brandConfig } = useBrandStore();
    const { data } = await $locationService.getLocations({ services: 'standard-pickup' });
    const locations = data || [];

    storeList.value = locations;
    storeIdMap.value = locations.reduce((obj: { [key: string]: StoreLocation }, store: StoreLocation) => {
      obj[store.id] = store;
      return obj;
    }, {});

    defaultStore.value = storeIdMap.value[brandConfig.defaultStoreId];

    return locations;
  };

  const getStore = (storeId: string) => computed<ErpStoreDetails>(() => storeIdMap.value[storeId] || {});
  // TODO: investigate if this is relevant / can be moved / refactored
  // Purpose - gets central storage or selected store, used for getting correct ordermultiples etc
  const getStoreIdForSelectedDeliveryId = computed(() => {
    const { getSelectedDeliveryId, selectedLocations } = useDeliveryStore();
    const { brandConfig } = useBrandStore();
    const deliveryId = getSelectedDeliveryId;
    const centralStorageId = brandConfig.centralStorageId;
    return STANDARD_DELIVERIES.includes(deliveryId) && centralStorageId
      ? centralStorageId
      : selectedLocations[deliveryId!];
  });

  return {
    defaultStore,
    storeList,
    storeIdMap,
    centralStorageId,
    setCentralStorageId,
    setStoreListFromLocationApi,
    getStore,
    getStoreIdForSelectedDeliveryId,
  };
});
